import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Container, Nav, Navbar, Offcanvas } from 'react-bootstrap';
import { ArrowLeftCircleFill, TelephoneFill, TicketPerforated } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Footer } from './Footer/Footer';
import PropTypes from 'prop-types';

// Константы для стилей
const NAV_STYLES = {
  BLACK: "nav-bg-black navbar-dark",
  WHITE: "nav-bg-white box-shadow",
  WHITE_NO_SHADOW: "nav-bg-white"
};

const LOGO_STYLES = {
  BLACK: "logo-black",
  WHITE: "logo-white"
};

// Константы для позиционирования
const NAV_POSITION = {
  FIXED: "fixed-top",
  STICKY: "sticky-top"
};

export const NavMenu = ({ 
  changanLanguage, 
  selLanguage, 
  setAboutMobMenu,
  isSticky = false // Опциональный пропс с значением по умолчанию false
}) => {
  const [bgNav, setBgNav] = useState(() => {
    // Если isSticky=true, начальный фон всегда белый
    return isSticky ? NAV_STYLES.WHITE : NAV_STYLES.BLACK;
  });
  
  const [logoColor, setLogoColor] = useState(() => {
    // Если isSticky=true, начальный логотип всегда черный
    return isSticky ? LOGO_STYLES.BLACK : LOGO_STYLES.WHITE;
  });
  
  const [show, setShow] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation("global");

  // Мемоизированные значения
  const isPayTicketsPage = useMemo(() => 
    location.pathname.includes("paytickets"), 
    [location.pathname]
  );

  const shouldUseWhiteTheme = useMemo(() => 
    isPayTicketsPage && isPortrait,
    [isPayTicketsPage, isPortrait]
  );

  // Обновление стилей навбара
  const updateNavStyles = useCallback((isScrolled, isMenuOpen, isPortraitMode, isPayPage) => {
    // Если isSticky=true, всегда используем белый фон
    if (isSticky) {
      setBgNav(NAV_STYLES.WHITE);
      setLogoColor(LOGO_STYLES.BLACK);
      return;
    }

    // Обычная логика для fixed режима
    if (isMenuOpen) {
      setBgNav(NAV_STYLES.WHITE_NO_SHADOW);
      setLogoColor(LOGO_STYLES.BLACK);
    } else if (isScrolled) {
      setBgNav(NAV_STYLES.WHITE);
      setLogoColor(LOGO_STYLES.BLACK);
    } else if (isPayPage && isPortraitMode) {
      setBgNav(NAV_STYLES.WHITE);
      setLogoColor(LOGO_STYLES.BLACK);
    } else {
      setBgNav(NAV_STYLES.BLACK);
      setLogoColor(LOGO_STYLES.WHITE);
    }
  }, [isSticky]);

  // Обработчик скролла (не нужен для sticky режима, но оставляем для совместимости)
  useEffect(() => {
    // Для sticky режима не нужно отслеживать скролл
    if (isSticky) return;

    const handleScroll = () => {
      const isScrolled = window.scrollY > 15;
      updateNavStyles(isScrolled, show, isPortrait, isPayTicketsPage);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [show, isPortrait, isPayTicketsPage, updateNavStyles, isSticky]);

  // Обновление при изменении show
  useEffect(() => {
    const isScrolled = window.scrollY > 15;
    updateNavStyles(isScrolled, show, isPortrait, isPayTicketsPage);
  }, [show, isPayTicketsPage, isPortrait, updateNavStyles]);

  // Определение ориентации экрана
  const checkOrientation = useCallback(() => {
    setIsPortrait(window.innerWidth < window.innerHeight);
  }, []);

  useEffect(() => {
    checkOrientation();
    
    const handleResize = () => {
      checkOrientation();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkOrientation]);

  // Закрытие меню при смене страницы
  useEffect(() => {
    setShow(false);
    setAboutMobMenu(false);
  }, [location.pathname, setAboutMobMenu]);

  const handleNavigation = useCallback((link) => {
    window.scrollTo(0, 0);
    navigate(link);
  }, [navigate]);

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleAboutClick = useCallback(() => {
    if (!isPortrait) {
      handleNavigation("/about-project");
    } else {
      setShow(false);
      setAboutMobMenu(true);
    }
  }, [isPortrait, handleNavigation, setAboutMobMenu]);

  const handleLanguageChange = useCallback(() => {
    changanLanguage(selLanguage === "en" ? "ru" : "en");
  }, [changanLanguage, selLanguage]);

  const renderLogo = useCallback((isMobile = false) => {
    const sizeStyle = isMobile 
      ? { maxWidth: "55px", maxHeight: "55px" }
      : { maxWidth: "150px", maxHeight: "55px" };
    
    const logoId = isMobile ? 'logo-small' : 'logo';

    return (
      <div className={logoColor}>
        <svg className='img-fluid' style={sizeStyle}>
          <use xlinkHref={`lc-logo.svg#${logoId}`}></use>
        </svg>
      </div>
    );
  }, [logoColor]);

  const renderLanguageButton = useCallback(() => (
    <Button 
      size='sm' 
      variant='light' 
      className='border'
      onClick={handleLanguageChange}
    >
      {selLanguage === "en" ? "RU" : "EN"}
    </Button>
  ), [selLanguage, handleLanguageChange]);

  // Определяем класс позиционирования
  const positionClass = isSticky ? NAV_POSITION.STICKY : NAV_POSITION.FIXED;

  return (
    <Navbar 
      className={`transition-nav p-0 ${positionClass} ${bgNav}`}
      expand={"lg"}
    >
      <Container fluid>
        {location.pathname !== '/' && (
          <div 
            style={{ inset: 0, margin: 'auto' }} 
            onClick={handleBack} 
            className='ms-1 me-3 d-lg-none'
            role="button"
            tabIndex={0}
            aria-label="Go back"
          >
            <ArrowLeftCircleFill 
              className={logoColor} 
              fill="#fff" 
              size={24} 
            />
          </div>
        )}
        
        <Navbar.Brand onClick={() => handleNavigation("/")} role="button">
          <div className='d-none d-lg-block'>
            {renderLogo(false)}
          </div>
          <div className='d-lg-none'>
            {renderLogo(true)}
          </div>
        </Navbar.Brand>

        <div className="text-center text-lg-end flex-fill me-3">
          <a className="text-decoration-none nav-link" href="tel:+78122429999">
            <p className="m-0">{t("menu.callforbooking")}</p>
            <p className="m-0">
              <TelephoneFill size={16} /> 8 (812) 242 9999
            </p>
          </a>
        </div>

        <Navbar.Toggle 
          onClick={() => setShow(prev => !prev)} 
          aria-controls="offcanvasNavbar-expand-lg" 
          aria-label="Toggle navigation"
        />

        <Navbar.Offcanvas
          className="offcanvas-nav-lc"
          id="offcanvasNavbar-expand-lg"
          aria-labelledby="offcanvasNavbarLabel-expand-lg"
          placement="end"
          show={show}
          onHide={() => setShow(false)}
        >
          <Offcanvas.Header closeButton closeVariant='white'>
            <Offcanvas.Title className='d-flex w-100 justify-content-around'>
              <div className='logo-white'>
                {renderLogo(false)}
              </div>
              <div className='ms-3 pt-2 pb-2 text-center'>
                {renderLanguageButton()}
              </div>
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Nav className="ms-auto justify-content-end navbar-dark text-center">
              <Nav.Link onClick={() => handleNavigation("/afisha")}>
                {t("menu.tickets")}
              </Nav.Link>
              
              <Nav.Link onClick={handleAboutClick}>
                {t("menu.about")}
              </Nav.Link>
              
              <Nav.Link onClick={() => handleNavigation("/about-project/corporate")}>
                {t("menu.corporate")}
              </Nav.Link>
              
              <Nav.Link onClick={() => handleNavigation("/news")}>
                {t("menu.news")}
              </Nav.Link>
              
              <Nav.Link className='d-lg-none' onClick={() => handleNavigation("/about-project/certificates")}>
                {t("menu.gifCertificates")}
              </Nav.Link>
              <Nav.Link 
                className='rounded-pill shadow border ps-2 pe-2 myTickets' 
                href='https://www.ticketspulse.ru/client/orders'
                // onClick={() => handleNavigation("/client/profile")}
              >
                <TicketPerforated 
                  style={{ transform: "rotate(-45deg)" }} 
                  className='me-1' 
                  size={20} 
                />
                {t("menu.mytickets")}
              </Nav.Link>
            </Nav>

            <div className='ms-lg-2 pt-2 pb-2 text-center d-none d-lg-block'>
              {renderLanguageButton()}
            </div>

            <div className='d-lg-none'>
              <Footer clearfix={true} />
            </div>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Container>
    </Navbar>
  );
};

// PropTypes для документации и валидации
NavMenu.propTypes = {
  changanLanguage: PropTypes.func.isRequired,
  selLanguage: PropTypes.string,
  setAboutMobMenu: PropTypes.func.isRequired,
  isSticky: PropTypes.bool
};

// Пример использования:
// <NavMenu 
//   changanLanguage={handleLanguageChange}
//   selLanguage={currentLanguage}
//   setAboutMobMenu={setAboutMenu}
//   isSticky={true} // для sticky режима с белым фоном
// />